import { useState } from 'react';

import {
  cancelStandaloneCharge,
  renewStandaloneCharge,
  resendStandaloneChargeEmail,
  StandaloneChargeDTO
} from '../../../services/standalone-charges';
import { dangerNotification, successNotification } from '../../../services/notification';

import { ListActionType } from '../types';

export const useChargeActions = (
  onReload?: () => void,
  onRenewed?: (charge: StandaloneChargeDTO) => void
) => {
  const [actionLoading, setActionLoading] = useState<{
    chargeId: string;
    action: ListActionType;
  } | null>(null);

  const copyChargeLink = async (link: string, chargeId?: string) => {
    if (chargeId) setActionLoading({ chargeId, action: 'copy' });
    try {
      const normalizedLink =
        typeof link === 'string' && link.trim()
          ? new URL(link, window.location.origin).toString()
          : link;
      await navigator.clipboard.writeText(normalizedLink);
      successNotification('Sucesso', 'Link copiado com sucesso!');
    } catch {
      dangerNotification('Erro', 'Não foi possível copiar o link.');
    } finally {
      if (chargeId) setActionLoading(null);
    }
  };

  const handleResendEmail = async (id: string) => {
    setActionLoading({ chargeId: id, action: 'resend' });
    try {
      await resendStandaloneChargeEmail(id);
      successNotification('Sucesso', 'E-mail reenviado com sucesso!');
      onReload?.();
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível reenviar e-mail.');
    } finally {
      setActionLoading(null);
    }
  };

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [chargeToCancel, setChargeToCancel] = useState<StandaloneChargeDTO | null>(null);

  const openCancelModal = (charge: StandaloneChargeDTO) => {
    setChargeToCancel(charge);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setChargeToCancel(null);
    setCancelModalOpen(false);
  };

  const confirmCancelCharge = async () => {
    if (!chargeToCancel) return;
    setActionLoading({ chargeId: chargeToCancel.id, action: 'cancel' });
    try {
      await cancelStandaloneCharge(chargeToCancel.id);
      successNotification('Sucesso', 'Cobrança cancelada com sucesso!');
      closeCancelModal();
      onReload?.();
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível cancelar cobrança.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRenewCharge = async (id: string) => {
    setActionLoading({ chargeId: id, action: 'renew' });
    try {
      const renewedCharge = await renewStandaloneCharge(id);
      successNotification('Sucesso', 'Cobrança renovada com sucesso!');
      onReload?.();
      onRenewed?.(renewedCharge);
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível renovar cobrança.');
    } finally {
      setActionLoading(null);
    }
  };

  return {
    actionLoading,
    copyChargeLink,
    handleResendEmail,
    cancelModalOpen,
    chargeToCancel,
    openCancelModal,
    closeCancelModal,
    confirmCancelCharge,
    handleRenewCharge
  };
};

